import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { persistLanguagePreference } from '../i18n';
import { 
  ArrowRightIcon, 
  CheckIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  ChartBarIcon,
  CogIcon,
  HomeIcon,
  Bars3Icon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';

const SECTION_IDS = ['home', 'main-content', 'partner-details', 'it-details', 'predictive-details', 'automation-details'] as const;

/** 뷰포트 세로 중앙이 걸린 섹션 id (없으면 null) */
function getSectionIdAtViewportCenter(): (typeof SECTION_IDS)[number] | null {
  const scrollPosition = window.scrollY + window.innerHeight / 2;
  for (let i = SECTION_IDS.length - 1; i >= 0; i--) {
    const id = SECTION_IDS[i];
    const element = document.getElementById(id);
    if (element) {
      const elementTop = element.offsetTop;
      const elementBottom = elementTop + element.offsetHeight;
      if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
        return id;
      }
    }
  }
  return null;
}

/** 섹션 안에 아직 스크롤할 여유가 있으면 true — 기본 스크롤 유지, 섹션 점프 금지 */
const SECTION_EDGE_PX = 80;

function shouldAllowNativeScrollInSection(sectionEl: HTMLElement, direction: 'down' | 'up'): boolean {
  const sectionTop = sectionEl.offsetTop;
  const sectionBottom = sectionTop + sectionEl.offsetHeight;
  const viewportTop = window.scrollY;
  const viewportBottom = viewportTop + window.innerHeight;

  if (direction === 'down') {
    return viewportBottom < sectionBottom - SECTION_EDGE_PX;
  }
  return viewportTop > sectionTop + SECTION_EDGE_PX;
}

/** 홈 히어로 첫 진입: 순차 등장 */
const homeHeroVariants = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.11, delayChildren: 0.06 },
    },
  },
  item: {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.48, ease: [0.22, 1, 0.36, 1] as const },
    },
  },
} as const;

const VANDRO_URL = 'https://vandro.ai/';

type PredictiveMetric = { label: string; description?: string; value?: string };
type PredictiveFeature = { title: string; bullets: string[] };

type LanguageCode = 'ko' | 'en' | 'ja' | 'es' | 'pt' | 'fr';
type LogoCard = {
  name: string;
  src: string;
  alt?: string;
  logoClassName?: string;
};

const customerLogos: LogoCard[] = [
  { name: '늘품마루', src: '/customer-logos/neulpum-maru.png' },
  { name: 'Mr.아빠', src: '/customer-logos/mr-appa.png' },
  { name: '푸드팡', src: '/customer-logos/foodpang.png' },
  { name: 'DELIVERY LAB', src: '/customer-logos/delivery-lab.png' },
];

const investorLogos: LogoCard[] = [
  {
    name: 'Antler',
    src: '/investor-logos/antler.png',
    logoClassName: 'max-h-12',
  },
];

const supportPartnerLogos: LogoCard[] = [
  {
    name: 'TBZ PARTNERS',
    src: '/partner-logos/tbz-partners.png',
    logoClassName: 'max-h-12',
  },
  {
    name: 'KG 이니시스',
    src: '/partner-logos/kg-inicis.png',
    logoClassName: 'max-h-16',
  },
  {
    name: '항공우주산학융합원',
    src: '/partner-logos/aerospace-collaboration.jpg',
    logoClassName: 'max-h-16',
  },
];

const overseasPartnerLogos: LogoCard[] = [
  {
    name: 'STARBURST',
    src: '/partner-logos/starburst.png',
    logoClassName: 'max-h-14',
  },
];

const partnerLogoGroups = [
  { logos: customerLogos, gridClassName: 'sm:grid-cols-2 xl:grid-cols-4' },
  { logos: investorLogos, gridClassName: 'sm:grid-cols-2 xl:grid-cols-4' },
  { logos: supportPartnerLogos, gridClassName: 'sm:grid-cols-2 xl:grid-cols-3' },
  { logos: overseasPartnerLogos, gridClassName: 'sm:grid-cols-2 xl:grid-cols-3' },
];

const Home: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('home');
  const [isScrolling, setIsScrolling] = useState(false);
  const [currentCard, setCurrentCard] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const partnerRows = (t('partnerNetwork.rows', { returnObjects: true }) as {
    title: string;
    eyebrow: string;
    description: string;
  }[]).map((row, index) => ({
    ...row,
    ...partnerLogoGroups[index],
  }));
  const automationDetailCards = t('automationDetails.cards', { returnObjects: true }) as {
    title: string;
    description: string;
    features: string[];
  }[];
  const automationDetailFlow = t('automationDetails.flow', { returnObjects: true }) as string[];

  const languages = [
    { code: 'ko' as LanguageCode, name: '한국어' },
    { code: 'en' as LanguageCode, name: 'English' },
    { code: 'ja' as LanguageCode, name: '日本語' },
    { code: 'es' as LanguageCode, name: 'Español' },
    { code: 'pt' as LanguageCode, name: 'Português' },
    { code: 'fr' as LanguageCode, name: 'Français' },
  ];

  const handleLanguageChange = (lang: LanguageCode) => {
    persistLanguagePreference(lang);
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const scrollToItDetails = () => {
    scrollToSection('it-details');
  };

  const scrollToPredictiveDetails = () => {
    scrollToSection('predictive-details');
  };

  const scrollToAutomationDetails = () => {
    scrollToSection('automation-details');
  };

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToMain = () => {
    scrollToSection('main-content');
  };

  const scrollToPartnerDetails = () => {
    scrollToSection('partner-details');
  };

  // 다음 섹션으로 이동 (뷰포트 기준 섹션 — 긴 섹션 중간에서는 state와 어긋날 수 있음)
  const scrollToNextSection = useCallback(() => {
    if (isScrolling) return;

    const activeId = getSectionIdAtViewportCenter() ?? (currentSection as (typeof SECTION_IDS)[number]);
    const currentIndex = SECTION_IDS.indexOf(activeId);
    if (currentIndex === -1) return;

    const nextIndex = Math.min(currentIndex + 1, SECTION_IDS.length - 1);
    const nextSection = SECTION_IDS[nextIndex];

    if (nextSection !== activeId) {
      setIsScrolling(true);
      scrollToSection(nextSection);
      setTimeout(() => setIsScrolling(false), 1000);
    }
  }, [isScrolling, currentSection, scrollToSection]);

  // 이전 섹션으로 이동
  const scrollToPrevSection = useCallback(() => {
    if (isScrolling) return;

    const activeId = getSectionIdAtViewportCenter() ?? (currentSection as (typeof SECTION_IDS)[number]);
    const currentIndex = SECTION_IDS.indexOf(activeId);
    if (currentIndex === -1) return;

    const prevIndex = Math.max(currentIndex - 1, 0);
    const prevSection = SECTION_IDS[prevIndex];

    if (prevSection !== activeId) {
      setIsScrolling(true);
      scrollToSection(prevSection);
      setTimeout(() => setIsScrolling(false), 1000);
    }
  }, [isScrolling, currentSection, scrollToSection]);

  const handleItButtonClick = () => {
    navigate('/it/');
  };

  type MainCardType = 'it' | 'vandro' | 'automation';

  // 캐러셀 관련 함수들
  const cards: {
    type: MainCardType;
    title: string;
    subtitle: string;
    badges: string[];
    description: string;
    button: string;
    icon: React.ComponentType<{ className?: string }>;
  }[] = [
    {
      type: 'it',
      title: t('main.itCard.title'),
      subtitle: t('main.itCard.subtitle'),
      badges: t('main.itCard.badges', { returnObjects: true }) as string[],
      description: t('main.itCard.description'),
      button: t('main.itCard.button'),
      icon: ComputerDesktopIcon,
    },
    {
      type: 'vandro',
      title: t('main.vandroCard.title'),
      subtitle: t('main.vandroCard.subtitle'),
      badges: t('main.vandroCard.badges', { returnObjects: true }) as string[],
      description: t('main.vandroCard.description'),
      button: t('main.vandroCard.button'),
      icon: CpuChipIcon,
    },
    {
      type: 'automation',
      title: t('main.automationCard.title'),
      subtitle: t('main.automationCard.subtitle'),
      badges: t('main.automationCard.badges', { returnObjects: true }) as string[],
      description: t('main.automationCard.description'),
      button: t('main.automationCard.button'),
      icon: CogIcon,
    },
  ];
  const cardCount = cards.length;

  const nextCard = useCallback(() => {
    setCurrentCard((prev) => (prev + 1) % cardCount);
  }, [cardCount]);

  const prevCard = () => {
    setCurrentCard((prev) => (prev - 1 + cardCount) % cardCount);
  };

  const goToCard = (index: number) => {
    setCurrentCard(index);
  };

  // 스크롤 위치 감지
  useEffect(() => {
    const handleScroll = () => {
      const id = getSectionIdAtViewportCenter();
      if (id) setCurrentSection(id);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 초기 실행

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 마우스 휠: 섹션 높이가 뷰포트보다 클 때는 끝까지 스크롤한 뒤에만 다음 섹션으로 이동
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (isScrolling) return;

      const sectionId = getSectionIdAtViewportCenter();
      if (!sectionId) return;

      const sectionEl = document.getElementById(sectionId);
      if (!sectionEl) return;

      const dir: 'down' | 'up' = e.deltaY > 0 ? 'down' : 'up';

      if (shouldAllowNativeScrollInSection(sectionEl, dir)) {
        return;
      }

      e.preventDefault();

      if (dir === 'down') {
        scrollToNextSection();
      } else {
        scrollToPrevSection();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isScrolling) return;

      if (e.key === 'Home') {
        e.preventDefault();
        scrollToTop();
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        scrollToSection('automation-details');
        return;
      }

      if (e.key !== 'ArrowDown' && e.key !== 'PageDown' && e.key !== 'ArrowUp' && e.key !== 'PageUp') {
        return;
      }

      const sectionId = getSectionIdAtViewportCenter();
      if (!sectionId) return;

      const sectionEl = document.getElementById(sectionId);
      if (!sectionEl) return;

      const dir: 'down' | 'up' =
        e.key === 'ArrowDown' || e.key === 'PageDown' ? 'down' : 'up';

      if (shouldAllowNativeScrollInSection(sectionEl, dir)) {
        return;
      }

      e.preventDefault();

      if (dir === 'down') {
        scrollToNextSection();
      } else {
        scrollToPrevSection();
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isScrolling, scrollToNextSection, scrollToPrevSection, scrollToTop, scrollToSection]);

  // 자동 롤링 기능
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      nextCard();
    }, 5000); // 5초마다 자동으로 다음 카드로 이동

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentCard, nextCard]);
  const cardAccent = (type: MainCardType) =>
    type === 'it'
      ? { left: 'border-l-blue-600', icon: 'text-blue-600', ring: 'ring-blue-600/10' }
      : type === 'vandro'
        ? { left: 'border-l-[#E36330]', icon: 'text-[#E36330]', ring: 'ring-[#E36330]/15' }
        : { left: 'border-l-emerald-600', icon: 'text-emerald-600', ring: 'ring-emerald-600/10' };

  const activeCardIndex = currentCard % cardCount;

  return (
    <div className="min-h-screen bg-zinc-50 pb-[max(7.5rem,env(safe-area-inset-bottom,0px)+6rem)] text-zinc-900 antialiased md:pb-0">
      {/* Language Selector */}
      <div className="fixed top-5 right-5 z-50">
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-800 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50"
          >
            <span>{languages.find((lang) => lang.code === i18n.language)?.name}</span>
            <svg
              className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isOpen && (
            <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
              <p className="border-b border-zinc-100 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-zinc-400">
                {t('languageSelector.label')}
              </p>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition-colors ${
                    lang.code === i18n.language
                      ? 'bg-zinc-100 font-medium text-zinc-900'
                      : 'text-zinc-600 hover:bg-zinc-50'
                  }`}
                >
                  <span>{lang.name}</span>
                  <span className="text-xs text-zinc-400">{lang.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Left Navigation */}
      <nav
        className="fixed left-4 top-1/2 z-40 hidden -translate-y-1/2 flex-col gap-1 rounded-xl border border-zinc-200 bg-white/95 p-1.5 shadow-sm backdrop-blur-sm md:flex"
        aria-label="Section"
      >
        {[
          { id: 'home' as const, icon: HomeIcon, label: t('homeBottomNav.home'), onClick: scrollToTop },
          { id: 'main-content' as const, icon: Bars3Icon, label: t('homeBottomNav.main'), onClick: scrollToMain },
          { id: 'partner-details' as const, icon: BuildingOffice2Icon, label: t('homeBottomNav.partner'), onClick: scrollToPartnerDetails },
          { id: 'it-details' as const, icon: ComputerDesktopIcon, label: t('homeBottomNav.it'), onClick: scrollToItDetails },
          { id: 'predictive-details' as const, icon: CpuChipIcon, label: t('homeBottomNav.vandro'), onClick: scrollToPredictiveDetails },
          { id: 'automation-details' as const, icon: CogIcon, label: t('homeBottomNav.automation'), onClick: scrollToAutomationDetails },
        ].map(({ id, icon: Icon, label, onClick }) => (
          <button
            key={id}
            type="button"
            onClick={onClick}
            className={`flex flex-col items-center gap-0.5 rounded-lg px-2.5 py-2 text-[10px] font-medium transition-colors ${
              currentSection === id
                ? 'bg-zinc-900 text-white'
                : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* Mobile bottom navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-200/90 bg-white/95 pb-[env(safe-area-inset-bottom,0px)] pt-1.5 pb-1.5 shadow-[0_-8px_32px_rgba(0,0,0,0.06)] backdrop-blur-md md:hidden"
        aria-label={t('homeBottomNav.ariaLabel')}
      >
        <div className="mx-auto flex max-w-lg items-stretch justify-between gap-0.5 px-2">
          {(
            [
              { id: 'home' as const, icon: HomeIcon, label: t('homeBottomNav.home'), onClick: scrollToTop },
              { id: 'partner-details' as const, icon: BuildingOffice2Icon, label: t('homeBottomNav.partner'), onClick: scrollToPartnerDetails },
              { id: 'it-details' as const, icon: ComputerDesktopIcon, label: t('homeBottomNav.it'), onClick: scrollToItDetails },
              { id: 'predictive-details' as const, icon: CpuChipIcon, label: t('homeBottomNav.vandro'), onClick: scrollToPredictiveDetails },
              { id: 'automation-details' as const, icon: CogIcon, label: t('homeBottomNav.automation'), onClick: scrollToAutomationDetails },
            ] as const
          ).map(({ id, icon: Icon, label, onClick }) => (
            <button
              key={id}
              type="button"
              onClick={onClick}
              className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-2 text-[10px] font-medium leading-tight transition-colors ${
                currentSection === id
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Hero */}
      <div
        id="home"
        className="relative flex min-h-screen flex-col items-center justify-center px-4 py-20 sm:px-6 lg:px-8"
      >
        <motion.div
          className="relative z-10 max-w-lg text-center"
          initial="hidden"
          animate="visible"
          variants={homeHeroVariants.container}
        >
          <motion.div
            variants={homeHeroVariants.item}
            className="mb-8 flex flex-col items-center gap-5 sm:flex-row sm:justify-center sm:gap-6"
          >
            <img
              src="/logo_1.jpg"
              alt="MJ"
              className="h-16 w-16 rounded-xl border border-zinc-200/80 object-contain shadow-sm sm:h-20 sm:w-20"
            />
            <div className="text-left sm:text-left">
              <h1 className="text-3xl font-semibold tracking-tight text-[#E36330] sm:text-4xl">
                MJ Corporation
              </h1>
              <p className="mt-2 text-base text-zinc-500 sm:text-lg">{t('logo.tagline')}</p>
            </div>
          </motion.div>

          <motion.p variants={homeHeroVariants.item} className="text-center text-sm text-zinc-400">
            {t('scrollIndicator.text')}
          </motion.p>
          {/* 아래로 이동(y 0→22px) + 지연 레이어로 궤적 잔상 */}
          <motion.div
            variants={homeHeroVariants.item}
            className="relative mx-auto mt-3 h-14 w-10"
            aria-hidden
          >
            {[
              { delay: 0.18, opacity: 0.22 },
              { delay: 0.09, opacity: 0.42 },
            ].map((trail, i) => (
              <motion.div
                key={i}
                className="pointer-events-none absolute left-1/2 top-0 h-10 w-6 -translate-x-1/2 rounded-full border border-zinc-400/50 bg-white/55 blur-[0.5px]"
                style={{ zIndex: i + 1, opacity: trail.opacity }}
                animate={{ y: [0, 22, 0] }}
                transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  ease: [0.42, 0, 0.58, 1],
                  delay: trail.delay,
                }}
              />
            ))}
            <motion.div
              className="absolute left-1/2 top-0 z-30 flex h-10 w-6 -translate-x-1/2 flex-col items-center overflow-hidden rounded-full border-2 border-zinc-400/80 bg-white/60 pt-2 shadow-sm"
              animate={{ y: [0, 22, 0] }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: [0.42, 0, 0.58, 1],
                delay: 0,
              }}
            >
              <div className="pointer-events-none absolute inset-x-1.5 top-2 bottom-2 rounded-full bg-zinc-200/35" />
              <div className="relative z-10 h-2 w-1 shrink-0 rounded-full bg-[#E36330] shadow-sm" />
          </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Main — business cards */}
      <div
        id="main-content"
        className="flex min-h-screen items-center border-t border-zinc-100 bg-white px-4 py-16 max-md:pb-[max(9rem,env(safe-area-inset-bottom,0px)+5.5rem)] sm:px-6 lg:px-8"
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-400">{t('main.title')}</p>
            <h2 className="mt-2 text-lg font-medium text-zinc-800 sm:text-xl">{t('main.subtitle')}</h2>
          </div>

          <div className="relative pt-8 sm:pt-0">
            <button
              type="button"
              onClick={prevCard}
              className="absolute left-0 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50 sm:flex"
              aria-label={t('carousel.previous')}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={nextCard}
              className="absolute right-0 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50 sm:flex"
              aria-label={t('carousel.next')}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="absolute -top-1 right-0 z-10 flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-colors hover:bg-zinc-50 sm:right-0"
              title={isAutoPlaying ? t('carousel.pause') : t('carousel.play')}
            >
              {isAutoPlaying ? (
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            <AnimatePresence mode="wait">
              <motion.article
                key={activeCardIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className={`mx-auto w-full max-w-4xl rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm ring-1 ring-zinc-950/5 sm:p-10 sm:px-12 border-l-4 ${cardAccent(cards[activeCardIndex].type).left}`}
              >
                {(() => {
                  const card = cards[activeCardIndex];
                  const IconComponent = card.icon;
                  const accent = cardAccent(card.type);
                  return (
                    <>
                      <div className="mb-6 flex justify-center">
                        <div
                          className={`rounded-xl bg-zinc-100 p-3 ring-1 ring-inset ring-zinc-900/5 ${accent.ring}`}
                        >
                          <IconComponent className={`h-9 w-9 ${accent.icon}`} />
                        </div>
                      </div>
                      <h3 className="text-center text-2xl font-semibold tracking-tight text-zinc-900">{card.title}</h3>
                      <p className="mt-2 text-center text-base text-zinc-600">{card.subtitle}</p>
                      <div className="mt-6 flex flex-wrap justify-center gap-2">
                        {card.badges.map((label) => (
                          <span
                            key={label}
                            className={
                              card.type === 'vandro'
                                ? 'rounded-md border border-[#E36330]/30 bg-[#E36330]/10 px-2.5 py-1 text-xs font-medium text-[#c24d28]'
                                : card.type === 'automation'
                                  ? 'rounded-md border border-emerald-600/25 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700'
                                : 'rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs font-medium text-zinc-700'
                            }
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                      <div className="mt-6 space-y-3 text-left text-sm leading-relaxed text-zinc-600">
                        {card.description
                          .split('\n')
                          .filter((line) => line.trim())
                          .map((line, lineIdx) => (
                            <p key={lineIdx}>{line.trim()}</p>
                          ))}
                      </div>
                      <div className="mt-8 flex justify-center">
                        {card.type === 'vandro' ? (
                          <a
                            href={VANDRO_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg bg-[#E36330] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#cc5629]"
                          >
                            {card.button}
                            <ArrowRightIcon className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (card.type === 'automation') {
                                navigate('/vandro-automation');
                              } else {
                                scrollToItDetails();
                              }
                            }}
                            className={`inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors ${
                              card.type === 'automation'
                                ? 'bg-emerald-600 hover:bg-emerald-700'
                                : 'bg-zinc-900 hover:bg-zinc-800'
                            }`}
                          >
                            {card.button}
                            <ArrowRightIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </motion.article>
            </AnimatePresence>

            <div className="mt-8 flex justify-center gap-2">
              {cards.map((c, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => goToCard(index)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeCardIndex === index
                      ? c.type === 'vandro'
                        ? 'w-8 bg-[#E36330]'
                        : c.type === 'automation'
                          ? 'w-8 bg-emerald-600'
                        : 'w-8 bg-zinc-900'
                      : 'w-1.5 bg-zinc-300 hover:bg-zinc-400'
                  }`}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Customers & Support Partners */}
      <section
        id="partner-details"
        className="flex min-h-screen items-center border-t border-orange-100 bg-white py-16 text-[#E36330] max-md:pb-[max(10rem,env(safe-area-inset-bottom,0px)+6.5rem)]"
      >
        <div className="mx-auto w-full max-w-[1680px] px-5 sm:px-8 lg:px-14 xl:px-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4 }}
            className="mb-12 max-w-none xl:mb-14"
          >
            <p className="text-sm font-medium uppercase tracking-[0.24em] text-[#E36330]/80 sm:text-base xl:whitespace-nowrap">
              {t('partnerNetwork.kicker')}
            </p>
            <h2 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-[#E36330] sm:text-5xl lg:text-[3.35rem] 2xl:text-6xl">
              <span className="block xl:whitespace-nowrap">{t('partnerNetwork.titleLine1')}</span>
              <span className="mt-4 block xl:whitespace-nowrap">{t('partnerNetwork.titleLine2')}</span>
            </h2>
            <p className="mt-7 max-w-none text-2xl leading-10 text-[#E36330]/85 [word-break:keep-all] sm:text-3xl sm:leading-[1.45]">
              <span className="block xl:whitespace-nowrap">{t('partnerNetwork.bodyLine1')}</span>
              <span className="block xl:whitespace-nowrap">{t('partnerNetwork.bodyLine2')}</span>
            </p>
          </motion.div>

          <div className="space-y-0">
            {partnerRows.map(({ title, eyebrow, description, logos, gridClassName }, rowIndex) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.4, delay: rowIndex * 0.04 }}
                className={`grid gap-6 py-8 lg:grid-cols-[380px_1fr] lg:items-center xl:grid-cols-[480px_1fr] 2xl:grid-cols-[500px_1fr] ${
                  rowIndex > 0 ? 'border-t border-zinc-200' : ''
                }`}
              >
                <div className="lg:pr-6 xl:pr-8 2xl:pr-10">
                  <h3 className="text-3xl font-bold tracking-[-0.06em] text-[#E36330] sm:text-4xl xl:whitespace-nowrap">{title}</h3>
                  <p className="mt-1 text-lg font-light tracking-[-0.02em] text-[#E36330]/70 xl:whitespace-nowrap">{eyebrow}</p>
                  <p className="mt-0.5 text-xl leading-7 tracking-[-0.045em] text-[#E36330]/90 [word-break:keep-all] xl:whitespace-nowrap">{description}</p>
                </div>
                <div className="border-t border-[#E36330] pt-5 lg:border-l-[3px] lg:border-t-0 lg:py-3 lg:pl-9">
                  <div className={`grid gap-6 ${gridClassName}`}>
                    {logos.map(({ name, src, alt, logoClassName }, index) => (
                      <motion.article
                        key={name}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.32, delay: rowIndex * 0.04 + index * 0.04 }}
                        className="flex h-24 items-center justify-center rounded border border-[#E36330]/55 bg-white px-7 py-5 shadow-[0_10px_24px_rgba(227,99,48,0.05)] transition-colors hover:border-[#E36330]"
                      >
                        <img
                          src={src}
                          alt={alt ?? t('common.logoAlt', { name })}
                          className={`max-h-14 max-w-full object-contain ${logoClassName ?? ''}`}
                          loading="lazy"
                        />
                      </motion.article>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* IT Solutions Detail Section */}
      <section
        id="it-details"
        className="flex min-h-screen items-center border-t border-zinc-100 bg-zinc-50/80 py-20 max-md:pb-[max(10rem,env(safe-area-inset-bottom,0px)+6.5rem)]"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4 }}
            className="mb-14 text-center"
          >
            <h2 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">{t('itDetails.title')}</h2>
            <p className="mx-auto mt-3 max-w-4xl text-lg text-zinc-600">{t('itDetails.subtitle')}</p>
          </motion.div>

          <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {(t('itDetails.services', { returnObjects: true }) as any[]).map((service: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="mb-4 flex justify-center">
                  {index === 0 && <ComputerDesktopIcon className="h-8 w-8 text-zinc-700" />}
                  {index === 1 && <CogIcon className="h-8 w-8 text-zinc-700" />}
                  {index === 2 && <ChartBarIcon className="h-8 w-8 text-zinc-700" />}
                </div>
                <h3 className="mb-2 text-center text-lg font-semibold text-zinc-900">{service.title}</h3>
                <p className="mb-4 text-center text-sm text-zinc-600">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature: any, featureIndex: number) => (
                    <li key={featureIndex} className="flex items-start gap-2 text-sm text-zinc-600">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleItButtonClick}
              className="rounded-lg bg-zinc-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
            >
              {t('itDetails.button')}
            </button>
          </div>
        </div>
      </section>

      {/* VANDRO */}
      <section
        id="predictive-details"
        className="flex min-h-screen items-center border-t border-zinc-100 bg-zinc-50/80 py-20 max-md:pb-[max(10rem,env(safe-area-inset-bottom,0px)+6.5rem)]"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4 }}
            className="mb-14 text-center"
          >
            <a
              href={VANDRO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mx-auto mb-8 inline-block rounded-xl border border-zinc-200/90 bg-white p-4 shadow-sm ring-1 ring-zinc-950/[0.04] transition-opacity hover:opacity-90"
            >
              <img
                src="/vandro-logo.png"
                alt="VANDRO"
                className="mx-auto h-10 w-auto max-w-[240px] object-contain sm:h-12"
                width={240}
                height={52}
              />
            </a>
            <h2 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">{t('predictiveDetails.title')}</h2>
            <p className="mx-auto mt-3 max-w-4xl text-lg text-zinc-600">{t('predictiveDetails.subtitle')}</p>
          </motion.div>

          <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {(t('predictiveDetails.metrics', { returnObjects: true }) as PredictiveMetric[]).map((metric, index) => (
              <motion.div
                key={`${metric.label}-${index}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="rounded-xl border border-zinc-200 bg-zinc-50/80 px-6 py-8 text-center"
              >
                {metric.value ? (
                  <>
                    <div className="text-3xl font-semibold tabular-nums text-[#E36330]">{metric.value}</div>
                    <div className="mt-1 text-sm font-medium text-zinc-800">{metric.label}</div>
                    {metric.description ? (
                      <p className="mt-2 text-left text-xs text-zinc-500">{metric.description}</p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="text-lg font-semibold text-[#E36330]">{metric.label}</div>
                    {metric.description ? (
                      <p className="mt-3 text-left text-sm leading-relaxed text-zinc-600">{metric.description}</p>
                    ) : null}
                  </>
                )}
              </motion.div>
            ))}
          </div>

          <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {(t('predictiveDetails.features', { returnObjects: true }) as PredictiveFeature[]).slice(0, 3).map((feature, index) => (
              <motion.div
                key={`${feature.title}-${index}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-zinc-900">{feature.title}</h3>
                <ul className="mt-3 list-disc space-y-1.5 pl-4 text-left text-sm leading-relaxed text-zinc-600">
                  {feature.bullets.map((line, i) => (
                    <li key={`${feature.title}-${i}`}>{line}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a
              href={VANDRO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex rounded-lg bg-[#E36330] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#cc5629]"
            >
              {t('predictiveDetails.button')}
            </a>
          </div>
        </div>
      </section>

      {/* VANDRO Automation Equipment */}
      <section
        id="automation-details"
        className="flex min-h-screen items-center border-t border-zinc-100 bg-white py-20 max-md:pb-[max(10rem,env(safe-area-inset-bottom,0px)+6.5rem)]"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4 }}
            className="mb-14 text-center"
          >
            <img
              src="/vandro-logo.png"
              alt="VANDRO"
              className="mx-auto mb-7 h-10 w-auto max-w-[220px] object-contain sm:h-12"
              width={220}
              height={48}
            />
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E36330]">{t('automationDetails.badge')}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              {t('automationDetails.title')}
            </h2>
            <p className="mx-auto mt-3 max-w-4xl text-lg text-zinc-600">
              {t('automationDetails.subtitle')}
            </p>
          </motion.div>

          <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {automationDetailCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="mb-4 flex justify-center">
                  {index === 0 && <CogIcon className="h-8 w-8 text-emerald-600" />}
                  {index === 1 && <CpuChipIcon className="h-8 w-8 text-[#E36330]" />}
                  {index === 2 && <CheckIcon className="h-8 w-8 text-blue-600" />}
                </div>
                <h3 className="mb-2 text-center text-lg font-semibold text-zinc-900">{card.title}</h3>
                <p className="mb-4 text-center text-sm leading-6 text-zinc-600">{card.description}</p>
                <ul className="space-y-2">
                  {card.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-zinc-600">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="rounded-xl border border-zinc-200 bg-zinc-950 p-6 text-white shadow-sm sm:p-8">
            <div className="grid gap-4 md:grid-cols-5">
              {automationDetailFlow.map((step, index) => (
                <div key={step} className="rounded-lg border border-white/10 bg-white/10 px-4 py-4 text-center">
                  <p className="text-xs font-semibold text-[#ffb08a]">{String(index + 1).padStart(2, '0')}</p>
                  <p className="mt-2 text-sm font-semibold">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate('/vandro-automation')}
              className="inline-flex items-center gap-2 rounded-lg bg-[#E36330] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#cc5629]"
            >
              {t('automationDetails.primaryButton')}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/vandro-automation#automation-inquiry')}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-6 py-3 text-sm font-medium text-zinc-800 transition-colors hover:bg-zinc-50"
            >
              {t('automationDetails.inquiryButton')}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Bottom Spacing */}
     
    </div>
  );
};

export default Home;
